import {
  ACADEMIC_SUBJECTS,
  GRADE_LEVELS,
  JURISDICTIONS,
  type AcademicSubject,
  type GradeLevel,
  type Jurisdiction,
} from '../../objectives/core/profile';

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The Standards check's state, subject and grade, as a group default or a material override.
 * Pure — no database, no request. The handlers read and write the JSON; this module decides
 * what it means (KTD2).
 *
 * Every field resolves on its own: the material override, else the group default, else empty
 * (R5). An override holds only the fields a run changed from the default, so a field nobody
 * touched keeps following the group when its default changes later (R6, R8).
 *
 * Values come only from the option lists in `objectives/core/profile.ts`. A stored value that
 * is not in them — stale or tampered — reads as empty, and one in a write is refused (R3).
 */

export interface StandardsContext {
  jurisdiction?: Jurisdiction;
  academicSubject?: AcademicSubject;
  grade?: GradeLevel;
}

export type ContextField = keyof StandardsContext;

/** The fields in their stored order. `serializeContext` writes them in this order. */
export const CONTEXT_FIELDS = ['jurisdiction', 'academicSubject', 'grade'] as const satisfies readonly ContextField[];

const OPTIONS: { [F in ContextField]: readonly string[] } = {
  jurisdiction: JURISDICTIONS,
  academicSubject: ACADEMIC_SUBJECTS,
  grade: GRADE_LEVELS,
};

/** Each field with its form label and option list, in stored order. Both forms render from this. */
export const CONTEXT_FIELD_METADATA: readonly { field: ContextField; label: string; options: readonly string[] }[] = [
  { field: 'jurisdiction', label: 'State', options: OPTIONS.jurisdiction },
  { field: 'academicSubject', label: 'Subject', options: OPTIONS.academicSubject },
  { field: 'grade', label: 'Grade', options: OPTIONS.grade },
];

/** How many fields a context sets. The `fields` count on the context-saved event. */
export const fieldCount = (ctx: StandardsContext | null): number =>
  ctx ? CONTEXT_FIELDS.filter((f) => ctx[f] !== undefined).length : 0;

/**
 * Where a resolved field came from. The form labels it (R9). `teacher` is the caller's own
 * shared standards default, held by Objectives, which fills only what the material and the
 * group leave empty (U5 R9). `planner` is the context a Planner hand-off carried, which
 * ranks between the group default and the teacher's own default (KTD5).
 */
export type ContextSource = 'override' | 'default' | 'planner' | 'teacher' | 'none';

export interface ResolvedContext {
  context: StandardsContext;
  source: Record<ContextField, ContextSource>;
}

const inOptions = (field: ContextField, value: unknown): boolean =>
  typeof value === 'string' && OPTIONS[field].includes(value);

const isEmpty = (ctx: StandardsContext): boolean => CONTEXT_FIELDS.every((f) => ctx[f] === undefined);

/** Copies one field from an untrusted record, only when its value is in the option list. */
const pick = (source: Record<string, unknown>, field: ContextField, into: StandardsContext): void => {
  if (inOptions(field, source[field])) (into as Record<ContextField, unknown>)[field] = source[field];
};

/** Copies one field between contexts that are already typed. */
const copy = <F extends ContextField>(from: StandardsContext, field: F, into: StandardsContext): void => {
  into[field] = from[field];
};

/**
 * Reads a stored `standards_context` column. NULL, malformed JSON, and a context with no
 * valid field all read as no context. A field outside the option lists is dropped, and keys
 * this module does not own are ignored.
 */
export const parseStoredContext = (json: string | null): StandardsContext | null => {
  if (!json) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  return isPlainObject(raw) ? pickContext(raw) : null;
};

/**
 * The fields of an untrusted record that are in the option lists, or null when none is. Keys
 * this module does not own are ignored.
 */
export const pickContext = (raw: Record<string, unknown>): StandardsContext | null => {
  const ctx: StandardsContext = {};
  for (const field of CONTEXT_FIELDS) pick(raw, field, ctx);
  return isEmpty(ctx) ? null : ctx;
};

export type ContextInputDecision =
  | { ok: true; context: StandardsContext }
  | { ok: false; reason: string };

/**
 * Checks a write body. Only the three fields; each may be missing, empty or null, which means
 * unset. A set value must be in its option list, and the grade is one value, not a list (R3).
 * A body with every field unset is valid and stores NULL.
 */
export const validateContextInput = (raw: unknown): ContextInputDecision => {
  if (!isPlainObject(raw)) return { ok: false, reason: 'Send an object with jurisdiction, academicSubject and grade.' };
  const extra = Object.keys(raw).filter((k) => !(CONTEXT_FIELDS as readonly string[]).includes(k));
  if (extra.length > 0) return { ok: false, reason: `Unknown field: ${extra.join(', ')}.` };
  const ctx: StandardsContext = {};
  for (const field of CONTEXT_FIELDS) {
    const value = raw[field];
    if (value === undefined || value === null || value === '') continue;
    if (!inOptions(field, value)) {
      return { ok: false, reason: `${field} must be one of: ${OPTIONS[field].join(', ')}.` };
    }
    pick(raw, field, ctx);
  }
  return { ok: true, context: ctx };
};

/**
 * The teacher's shared default (Objectives' shape) as a Collab context. A value outside the
 * option lists is dropped, and of several grades the lowest in `GRADE_LEVELS` order is taken,
 * because a Standards check runs on one grade (R6, KTD7). Null when no field survives.
 */
export const teacherContext = (shared: {
  jurisdiction?: string; academicSubject?: string; gradeLevels?: readonly string[];
} | null): StandardsContext | null => {
  if (!shared) return null;
  const ctx: StandardsContext = {};
  pick(shared, 'jurisdiction', ctx);
  pick(shared, 'academicSubject', ctx);
  const grades = Array.isArray(shared.gradeLevels) ? shared.gradeLevels : [];
  const grade = GRADE_LEVELS.find((g) => grades.includes(g));
  if (grade) ctx.grade = grade;
  return isEmpty(ctx) ? null : ctx;
};

/**
 * Each field from the override, else the group default, else the Planner's hand-off, else the
 * teacher's own default, else empty (R5; U5 R9; KTD5). With no teacher and no Planner layer
 * this is the two-layer resolution it always was.
 *
 * The Planner layer is last in the argument list only so existing callers keep their meaning;
 * it ranks above `teacher`. It lives only in the browser — the Worker never sees it — so a
 * run with a Planner value is saved like any value the member chose.
 */
export const resolveContext = (
  override: StandardsContext | null,
  groupDefault: StandardsContext | null,
  teacher: StandardsContext | null = null,
  planner: StandardsContext | null = null,
): ResolvedContext => {
  const context: StandardsContext = {};
  const source = {} as Record<ContextField, ContextSource>;
  const layers: readonly [StandardsContext | null, ContextSource][] = [
    [override, 'override'], [groupDefault, 'default'], [planner, 'planner'], [teacher, 'teacher'],
  ];
  for (const field of CONTEXT_FIELDS) {
    const hit = layers.find(([layer]) => layer?.[field] !== undefined);
    if (hit) copy(hit[0]!, field, context);
    source[field] = hit ? hit[1] : 'none';
  }
  return { context, source };
};

/**
 * The context a Standards check ran with. A run that does not carry exactly one grade gives
 * nothing, so it saves no override (KTD3).
 */
export const contextFromRun = (profile: {
  jurisdiction: Jurisdiction;
  academicSubject: AcademicSubject;
  gradeLevels: readonly GradeLevel[];
}): StandardsContext | null => {
  const [grade] = profile.gradeLevels;
  if (grade === undefined || profile.gradeLevels.length !== 1) return null;
  return { jurisdiction: profile.jurisdiction, academicSubject: profile.academicSubject, grade };
};

/**
 * The override a run stores: only the fields that differ from the group default, or null when
 * none do (R6). A field equal to the default is left out so it keeps following the group.
 *
 * A field that came only from the caller's own teacher default — the current override and the
 * group both leave it empty, and the run kept the teacher's value — is left out too. The
 * teacher layer is one person's default; it must never become the material's value for
 * everyone in the group (KTD11). With no `teacher` this is the group-only rule it always was.
 */
export const overrideToStore = (
  run: StandardsContext,
  groupDefault: StandardsContext | null,
  teacher: StandardsContext | null = null,
  current: StandardsContext | null = null,
): StandardsContext | null => {
  const { source } = resolveContext(current, groupDefault, teacher);
  const ctx: StandardsContext = {};
  for (const field of CONTEXT_FIELDS) {
    if (run[field] === undefined || run[field] === groupDefault?.[field]) continue;
    if (source[field] === 'teacher' && run[field] === teacher?.[field]) continue;
    copy(run, field, ctx);
  }
  return isEmpty(ctx) ? null : ctx;
};

/**
 * Whether a run changed any value from what the form resolved, teacher layer included. Equal
 * values write nothing (AE6).
 */
export const shouldWriteOverride = (
  run: StandardsContext,
  override: StandardsContext | null,
  groupDefault: StandardsContext | null,
  teacher: StandardsContext | null = null,
): boolean => {
  const { context: resolved } = resolveContext(override, groupDefault, teacher);
  return CONTEXT_FIELDS.some((f) => run[f] !== resolved[f]);
};

/** The column value for a context: JSON in field order, or NULL when no field is set. */
export const serializeContext = (ctx: StandardsContext | null): string | null => {
  if (!ctx || isEmpty(ctx)) return null;
  const ordered: StandardsContext = {};
  for (const field of CONTEXT_FIELDS) if (ctx[field] !== undefined) copy(ctx, field, ordered);
  return JSON.stringify(ordered);
};
